'use client'

import React from 'react'

import { ArrowButton } from '@/components/ui/arrow-button'
import { useCarouselScroll } from '@/hooks/useCarouselScroll'
import { cn } from '@/utilities/ui'

import { AmbassadorCard, type AmbassadorData } from './AmbassadorCard'

interface Props {
  ambassadors: AmbassadorData[]
}

/**
 * Layout:
 *  - lg+ (desktop): 3-column grid, no carousel controls
 *  - <lg (tablet/mobile): horizontal scroll-snap carousel, shows ~2-up on md and 1-up with peek on mobile
 */
export const AmbassadorsGrid: React.FC<Props> = ({ ambassadors }) => {
  const count = ambassadors.length
  const tabletIsCarousel = count > 2
  const mobileIsCarousel = count > 1
  const needsCarouselControls = tabletIsCarousel || mobileIsCarousel

  const { scrollRef, activeIndex, isAtStart, isAtEnd, scrollToIndex, handleKeyDown } =
    useCarouselScroll({ itemCount: count, autoplayDelay: 0 })

  return (
    <div className="flex flex-col gap-6 lg:gap-0 w-full">
      <div
        ref={scrollRef}
        role={needsCarouselControls ? 'region' : undefined}
        aria-label={needsCarouselControls ? 'Civo ambassadors carousel' : undefined}
        aria-roledescription={needsCarouselControls ? 'carousel' : undefined}
        tabIndex={needsCarouselControls ? 0 : undefined}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex w-full gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide',
          'lg:grid lg:grid-cols-3 lg:overflow-visible',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-lime focus-visible:ring-offset-2',
        )}
      >
        {ambassadors.map((ambassador, index) => (
          <div
            key={ambassador.id ?? index}
            data-card
            role={needsCarouselControls ? 'group' : undefined}
            aria-roledescription={needsCarouselControls ? 'slide' : undefined}
            aria-label={needsCarouselControls ? `${index + 1} of ${count}` : undefined}
            className={cn(
              'shrink-0 snap-start flex',
              'basis-[85%] md:basis-[calc((100%-24px)/2)]',
              'lg:basis-auto lg:shrink',
            )}
          >
            <AmbassadorCard ambassador={ambassador} />
          </div>
        ))}
      </div>

      {needsCarouselControls && (
        <>
          <div
            className="flex lg:hidden items-center gap-2 self-end"
            role="group"
            aria-label="Carousel navigation"
          >
            <ArrowButton
              direction="left"
              backgroundColor="lime"
              size="medium"
              animated
              onClick={() => scrollToIndex(activeIndex - 1)}
              disabled={isAtStart}
              aria-label="Previous ambassadors"
            />
            <ArrowButton
              direction="right"
              backgroundColor="lime"
              size="medium"
              animated
              onClick={() => scrollToIndex(activeIndex + 1)}
              disabled={isAtEnd}
              aria-label="Next ambassadors"
            />
          </div>

          <div className="sr-only lg:hidden" aria-live="polite" aria-atomic="true">
            {`Ambassador ${activeIndex + 1} of ${count}`}
          </div>
        </>
      )}
    </div>
  )
}
